/**
 * URL Change Invalidator Plugin
 * 
 * Automatically invalidates React Query cache when URL parameters change.
 * Bridges appRuntime service with React Query cache invalidation.
 * 
 * ## How It Works
 * 
 * 1. Subscribes to appRuntime parameter changes (club-id, tenant-handle)
 * 2. When parameters change, automatically invalidates relevant queries
 * 3. Uses hybrid scoping strategy to invalidate correct queries
 * 
 * ## Invalidation Strategy
 * 
 * **Club Change** (club-id parameter):
 * - Invalidates: Club-scoped queries for OLD club only
 * - Preserves: Tenant-scoped queries (payment methods, user profile)
 * - Preserves: Global queries (auth session)
 * 
 * **Tenant Change** (tenant-handle parameter):
 * - Invalidates: EVERYTHING (nuclear option)
 * - Prevents: Cross-tenant data leaks
 * - Reason: Different organization = fresh start
 * 
 * ## Usage
 * 
 * In main.tsx - one-time setup:
 * 
 * import { registerUrlChangeInvalidation } from '~/lib/query/plugins';
 * import { QueryClient } from '@tanstack/react-query';
 * 
 * const queryClient = new QueryClient({...});
 * registerUrlChangeInvalidation(queryClient);
 * 
 * Done! Automatic invalidation now works everywhere
 * 
 * ## What Gets Invalidated
 * 
 * ### Club Change Example
 * User at: ?club-id=2&tenant-handle=drc
 * Navigation: ?club-id=5&tenant-handle=drc
 * 
 * Invalidated:
 * - ['clubId', 2, 'bookings', ...]      // Club 2 bookings
 * - ['clubId', 2, 'bays', ...]          // Club 2 bays
 * - ['clubId', 2, 'subscriptions', ...] // Club 2 subscriptions
 * - ['clubId', 2, 'memberships', ...]   // Club 2 memberships
 * 
 * Preserved:
 * - ['tenantHandle', 'drc', 'paymentMethods', ...] // Payment methods
 * - ['auth', 'currentUser']                        // Auth session
 * - ['clubId', 5, ...]                             // Club 5 data (new)
 * 
 * ### Tenant Change Example
 * User at: drc.example.com?club-id=2
 * Navigation: golfclub.example.com?club-id=1
 * 
 * Result: queryClient.clear() - everything wiped
 * 
 * @module query/plugins
 */

import type { QueryClient } from '@tanstack/react-query';
import { appRuntime, CLUB_ID_PARAM, TENANT_HANDLE_PARAM } from '~/services/appRuntime';

/**
 * Register automatic cache invalidation on URL parameter changes
 * 
 * Call this ONCE during app initialization (in main.tsx).
 * It will automatically invalidate queries when clubId or tenantHandle changes.
 * 
 * @param queryClient - React Query client instance
 * @returns Cleanup function to unsubscribe from parameter changes
 * 
 * @example
 * // In main.tsx
 * const queryClient = new QueryClient({...});
 * registerUrlChangeInvalidation(queryClient);
 */
export function registerUrlChangeInvalidation(queryClient: QueryClient): () => void {
  // Track previous values to detect changes
  let prevClubId = appRuntime.clubId;
  let prevTenantHandle = appRuntime.tenantHandle;

  // Subscribe to club-id changes
  const unsubscribeClub = appRuntime.subscribe(CLUB_ID_PARAM, (newClubId) => {
    const oldClubId = prevClubId;
    
    // Only invalidate if club actually changed (not initial load)
    if (oldClubId && newClubId !== oldClubId) {
      console.log('[QueryInvalidator] Club changed:', {
        from: oldClubId,
        to: newClubId,
        action: 'Invalidating club-scoped queries'
      });

      // Invalidate club-scoped queries for the OLD club
      // This ensures user doesn't see stale data from previous club
      // IMPORTANT: Use refetchType: 'none' to prevent cache poisoning
      // Many queryFns read appRuntime.clubId at execution time, so refetching
      // old-club queries after the URL changes would fetch NEW-club data and
      // store it under the OLD club's queryKey
      queryClient.invalidateQueries({
        refetchType: 'none',
        predicate: (query) => {
          const key = query.queryKey;
          
          // Match standard pattern: ['clubId', oldClubId, ...]
          // All club-scoped queries follow this pattern for automatic invalidation
          // Examples:
          // - ['clubId', 2, 'bookings', ...]
          // - ['clubId', 2, 'bays', 'availability', ...]
          // - ['clubId', 2, 'booking', 'validation', ...]
          // - ['clubId', 2, 'subscriptions', 'active', 'user', 123]
          const isClubScoped = key[0] === 'clubId';
          const matchesOldClub = key[1] === oldClubId || key[1] === Number(oldClubId);
          
          return isClubScoped && matchesOldClub;
        }
      });

      console.log('[QueryInvalidator] Club-scoped queries invalidated for club:', oldClubId);
    }
    
    // Update tracking
    prevClubId = newClubId;
  });

  // Subscribe to tenant-handle changes
  const unsubscribeTenant = appRuntime.subscribe(TENANT_HANDLE_PARAM, (newTenantHandle) => {
    const oldTenantHandle = prevTenantHandle;
    
    // Only clear if tenant actually changed (not initial load)
    if (oldTenantHandle && newTenantHandle !== oldTenantHandle) {
      console.warn('[QueryInvalidator] Tenant changed:', {
        from: oldTenantHandle,
        to: newTenantHandle,
        action: 'CLEARING ALL QUERIES (nuclear option)'
      });

      // Nuclear option: Clear EVERYTHING
      // This is critical for security - prevents cross-tenant data leaks
      queryClient.clear();

      console.warn('[QueryInvalidator] All queries cleared due to tenant change');
    }
    
    // Update tracking
    prevTenantHandle = newTenantHandle;
  });

  // Return cleanup function
  return () => {
    unsubscribeClub();
    unsubscribeTenant();
  };
}
